import React from 'react';
import { IAudition } from '../../types/global/helper';
import { Avatar, Box, Card, CardMedia, Chip, Grid, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { StarRating } from '../EsVideoVotingModal/EsRatingStars';
import { isEmpty } from '../../utils/lodash';
import CustomButton from '../Button/CustomButton';
import { convertMinuteSecondsDays } from '../../utils/AppUtils';
import { BackgroundColorCard } from '../Cards/BackGroundColorCard';
import { CompanyAvatar } from '../Avatar/CompanyAvatar';
import { RichTextViewer } from '../Common/RichTextViewer';

interface IAuditionCard {
    audition: IAudition;
    onViewAndApplyClick: () => void;
}

export const AuditionCard: React.FC<IAuditionCard> = ({ audition, onViewAndApplyClick }) => {
    const { themeStyle } = useThemeContext();
    return (
        <Stack border={`0.5px solid ${theme[themeStyle].tag_white}`} overflow={'hidden'} borderRadius={2}>
            <Card sx={{ overflow: 'hidden', objectFit: 'cover', position: 'relative' }}>
                {audition.compensation && !isEmpty(audition.compensation) && (
                    <Chip
                        label={audition.compensation === 'paid' ? '$$$$' : audition.compensation}
                        sx={{
                            position: 'absolute',
                            right: 5,
                            top: 5,
                            color: theme[themeStyle].true_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 8,
                            borderRadius: 1,
                            p: 0,
                            backgroundColor:
                                audition.compensation === 'paid'
                                    ? theme[themeStyle].primaryGreen
                                    : theme[themeStyle].secondary_red,
                        }}
                    />
                )}
                {audition.imageCover && (
                    <Box sx={{ position: 'relative' }}>
                        <CardMedia
                            component="img"
                            image={audition.imageCover}
                            alt={`${audition.title}_image`}
                            sx={{
                                objectFit: 'cover',
                                height: '250px',
                                width: '100%',
                            }}
                        />
                        {/* Union affiliation and type of work chips */}
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                position: 'absolute',
                                top: 5,
                                left: 5,
                                zIndex: 1,
                            }}>
                            {audition.union_affiliation && (
                                <Chip
                                    label={audition.union_affiliation}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 8,
                                        height: '20px',
                                    }}
                                />
                            )}
                            {audition.type_of_work && (
                                <Chip
                                    label={audition.type_of_work}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 8,
                                        height: '20px',
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
                {!audition?.imageCover && audition?.is_background_color && audition.background_color && (
                    <BackgroundColorCard
                        backgroundColor={audition.background_color}
                        text={audition.title}
                        sx={{
                            height: '250px',
                            width: '100%',
                            my: 0,
                        }}
                    />
                )}
            </Card>
            <Stack p={2}>
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontSize: 14,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                        marginBottom: 5,
                    }}>
                    {audition.title}
                </Typography>

                <RichTextViewer
                    content={audition.synopsis}
                    textStyle={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 3,
                        marginBottom: 5,
                        paddingLeft: 0,
                    }}
                />
                {audition.usage && audition.usage.length > 0 && (
                    <Box mb={1}>
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                                marginBottom: 4,
                            }}>
                            Usage:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {audition.usage.map((usageItem, index) => (
                                <Chip
                                    key={index}
                                    label={usageItem}
                                    size="small"
                                    sx={{
                                        backgroundColor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        height: '24px',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
                <Box>
                    <Typography
                        style={{
                            fontSize: 10,
                            color: theme[themeStyle].tag_white,
                            opacity: 0.5,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontStyle: 'italic',
                        }}>
                        Posted {convertMinuteSecondsDays(audition.created)} ago
                    </Typography>
                </Box>
                {audition.roles && !isEmpty(audition.roles) && (
                    <Stack mb={2}>
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                color: theme[themeStyle].tag_white,
                            }}>
                            Roles:
                        </Typography>
                        <Grid container spacing={2}>
                            {audition.roles.map((role, index) => {
                                const labelParts = [
                                    role.character && `${role.character}`,
                                    role.gender && `${role.gender}`,
                                    (role.min_age || role.max_age) && `${role.min_age || ''}-${role.max_age || ''}`,
                                ].filter(Boolean); // Filter out undefined or empty parts
                                return (
                                    <Grid item key={index}>
                                        <Chip
                                            avatar={<Avatar>{role.character.charAt(0).toUpperCase()}</Avatar>}
                                            label={[labelParts.join(' - ')]}
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                            }}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Stack>
                )}

                <Stack direction="row" alignItems="center" spacing={1}>
                    <CompanyAvatar company={audition?.production_company} enableDefaultNavigation />
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 10,
                                color: theme[themeStyle].tag_white,
                            }}>
                            {audition?.production_company.company_name}
                        </Typography>
                    </Box>
                    <StarRating
                        disabled
                        style={{
                            paddingLeft: 0,
                            paddingRight: 0,
                            height: '10px',
                            width: 'fit-content',
                        }}
                        ratingProps={{
                            value: audition?.production_company.rating,
                            readOnly: true,
                            sx: {
                                fontSize: 14,
                                color: theme[themeStyle].highlight,
                            },
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 10,
                            color: theme[themeStyle].tag_white,
                        }}>
                        {audition?.production_company.number_of_reviews}
                    </Typography>
                </Stack>

                <Stack>
                    <CustomButton
                        label="View & Apply"
                        sx={{
                            width: '100%',
                            mt: 2,
                        }}
                        onClick={onViewAndApplyClick}
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};
